import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ReportsTable } from '../ReportGroup';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { extractReadableNameFromObject } from 'shared/utils/stringUtils';

const SimpleRender: React.FC<{
  name: string | number | undefined | null;
  secondary?: boolean;
  color?: keyof typeof COLORS;
  isBold?: boolean;
  marginBottom?: boolean;
  value: string | number | undefined | null;
}> = ({
  name,
  value,
  isBold = false,
  secondary = false,
  marginBottom = true,
  color = COLORS.blackBase,
}) => {
  return (
    <div
      className="render"
      style={
        secondary
          ? {
              paddingLeft: SPACERS.large,
              fontWeight: isBold ? 'bold' : undefined,
              marginBottom: marginBottom ? undefined : 0,
            }
          : undefined
      }
    >
      {secondary && <span>• </span>}
      <strong>{name}:</strong>{' '}
      <span style={{ color: color }}>
        {value ? (
          extractReadableNameFromObject(value)
        ) : (
          <span style={{ color: COLORS.critical }}>undefined</span>
        )}
      </span>
    </div>
  );
};

interface ReportRawValuesProps {
  report: ReportsTable;
}

const StyledDiv = styled.div`
  padding: ${SPACERS.extraLarge};
  .render {
    margin-bottom: ${SPACERS.medium};
  }
`;

const ReportRawValues: React.FC<ReportRawValuesProps> = ({ report }) => {
  const [showRawValues, setShowRawValues] = useState<boolean>(false);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        (event.ctrlKey || event.metaKey) &&
        event.altKey &&
        event.code === 'KeyV'
      ) {
        setShowRawValues(o => !o);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [setShowRawValues]);

  if (!showRawValues) return null;

  return (
    <StyledDiv>
      <SimpleRender name="Report Id" value={report.id} />
      <SimpleRender name="Program" value={report.name} />
      <SimpleRender name="Year" value={report.year} />
      <SimpleRender name="Start Date" value={report.startDate} />
      <SimpleRender name="End Date" value={report.endDate} />
      <SimpleRender
        name="Beneficiary Description"
        value={report.beneficiaryDescription}
      />
      {(report.beneficiaryLocations || []).length > 0 ? (
        <strong>Beneficiary Locations:</strong>
      ) : (
        <div style={{ color: COLORS.critical }}>No beneficiary locations</div>
      )}
      {(report.beneficiaryLocations || []).map((l, i) => (
        <SimpleRender
          key={`beneficiary-locations-${i}`}
          name={i}
          value={l.location}
        />
      ))}
      {report.results.length > 0 ? (
        <strong>Impact Model:</strong>
      ) : (
        <div style={{ color: COLORS.critical }}>No impact model found</div>
      )}
      {report.results.map(l => (
        <div key={l.dbId}>
          <SimpleRender
            name="Initial Forecast"
            secondary={true}
            marginBottom={false}
            value={l.forecastInitial}
          />
          <SimpleRender
            name="Current Forecast"
            secondary={true}
            marginBottom={false}
            value={l.forecastCurrent}
          />
          <SimpleRender
            name="Success Criteria"
            secondary={true}
            marginBottom={false}
            value={l.successCriteria}
          />
          <SimpleRender
            name="Stage Name"
            isBold={true}
            secondary={true}
            marginBottom={false}
            color="success"
            value={l.indicator.stgName}
          />
          <SimpleRender
            name="Indicator"
            secondary={true}
            marginBottom={false}
            value={l.indicator.name}
          />
          <SimpleRender
            secondary={true}
            name="Indicator Definition"
            value={l.indicator.definition}
          />
        </div>
      ))}
      {report.notes.length > 0 ? (
        <strong>Notes:</strong>
      ) : (
        <div style={{ color: COLORS.critical }}>No notes</div>
      )}
      {report.notes.map((n, i) => (
        <div key={n.id}>
          <SimpleRender
            secondary={true}
            name="Type"
            value={n.type}
            marginBottom={false}
          />
          <SimpleRender
            secondary={true}
            name="Created At"
            value={n.createdAt}
            marginBottom={false}
          />
          <SimpleRender secondary={true} name="Message" value={n.message} />
        </div>
      ))}
      <SimpleRender
        name="Cost Comment Direct Cash"
        value={report.costCommentDirectCash}
      />
      <SimpleRender
        name="Cost Comment Direct In-Kind"
        value={report.costCommentDirectInKind}
      />
      <SimpleRender
        name="Cost Comment Indirect Cash"
        value={report.costCommentIndirectCash}
      />
      <SimpleRender
        name="Cost Comment Indirect In-Kind"
        value={report.costCommentIndirectInKind}
      />
      <SimpleRender
        name="Cost Current Direct Cash"
        value={report.costCurrentDirectCash}
      />
      <SimpleRender
        name="Cost Current Direct In-Kind"
        value={report.costCurrentDirectInKind}
      />
      <SimpleRender
        name="Cost Current Indirect Cash"
        value={report.costCurrentIndirectCash}
      />
      <SimpleRender
        name="Cost Current Indirect In-Kind"
        value={report.costCurrentIndirectInKind}
      />
      <SimpleRender
        name="Cost Initial Direct Cash"
        value={report.costInitialDirectCash}
      />
      <SimpleRender
        name="Cost Initial Direct In-Kind"
        value={report.costInitialDirectInKind}
      />
      <SimpleRender
        name="Cost Initial Indirect Cash"
        value={report.costInitialIndirectCash}
      />
      <SimpleRender
        name="Cost Initial Indirect In-Kind"
        value={report.costInitialIndirectInKind}
      />
      <SimpleRender
        name="Intervention Dosage Level"
        value={report.interventionDosageLevel}
      />
      <SimpleRender
        name="Intervention Dosage Number"
        value={report.interventionDosageNumber}
      />
      <SimpleRender
        name="Intervention Dosage Type"
        value={report.interventionDosageType}
      />
      <SimpleRender
        name="Primary Intervention Description"
        value={report.primaryInterventionDescription}
      />
      <SimpleRender
        name="Primary Intervention Sub-types"
        value={report.primaryInterventionSubtypes}
      />
      <SimpleRender
        name="Primary Intervention Type"
        value={report.primaryInterventionType}
      />
      <SimpleRender
        name="Taxonomy Subject Id"
        value={report.taxonomySubjectId}
      />
      <SimpleRender
        name="Taxonomy Subject Level"
        value={report.taxonomySubjectLevel}
      />
    </StyledDiv>
  );
};

export default ReportRawValues;
